// PreCallBriefingPanel — the 30-second "get ready to call this
// person" briefing that lives at the top of the client profile.
//
// Renders three sections:
//   1. Warm briefing paragraph (LLM-narrative or deterministic
//      fallback)
//   2. Talking points (3-5 short bullets the agent can glance at
//      while dialing)
//   3. Recent context + coverage gaps (fact chips)
//
// Fires on mount + on-demand refresh. Cached in component state so
// closing/reopening the modal within a session doesn't re-call
// Claude for the same client.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || body?.detail || `HTTP ${res.status}`);
  return body;
}

export default function PreCallBriefingPanel({ clientId }) {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const r = await apiFetch('/api/ai-ceo/pre-call', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId }),
      });
      setData(r);
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setLoading(false); }
  }, [clientId]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-xl text-white">✦</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Pre-call briefing</div>
            <div className="text-[13px] text-slate-500 italic mt-0.5">Assembling what we know about this client…</div>
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="border-2 border-rose-200 bg-rose-50 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <span className="text-lg">⚠</span>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">Pre-call briefing failed</div>
            <div className="text-[13px] text-rose-700 mt-0.5">{err}</div>
          </div>
          <button onClick={load} className="text-[11px] font-semibold text-rose-700 underline">Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { briefing, talking_points, recent_context, coverage_gaps, synthesized } = data;

  // FTPIG demo styling: clean bordered card with an eyebrow row
  // (✦ label on the left + "for your next conversation" on the
  // right) and a flat body paragraph. No gradient, no colored
  // border — just a subtle slate border and the ✦ mark.
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      {/* Eyebrow */}
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[11.5px] font-bold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-1.5">
          <span className="text-[#e8a020]">✦</span>
          <span>Pre-call briefing</span>
          {!synthesized && (
            <span className="text-slate-400 lowercase tracking-normal italic font-normal">· fact fallback</span>
          )}
        </div>
        <div className="text-[11.5px] text-slate-500 italic">for your next conversation</div>
      </div>

      {/* Narrative */}
      {briefing && (
        <div className="mt-3 text-[13.5px] leading-relaxed text-slate-800 whitespace-pre-wrap">
          {briefing}
        </div>
      )}

      {/* Talking points — only render when synthesized returned some
          because the demo doesn't stack extra sections; keep it clean. */}
      {Array.isArray(talking_points) && talking_points.length > 0 && (
        <ul className="mt-3 space-y-1">
          {talking_points.map((tp, i) => (
            <li key={i} className="text-[12.5px] text-slate-700 flex gap-2">
              <span className="text-[#e8a020] shrink-0">→</span>
              <span>{tp}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Refresh + collapse — hidden by default, revealed on hover
          via a small opacity trick. Keeps the clean demo look but
          preserves the actions when needed. */}
      <div className="mt-3 flex justify-end gap-3 text-[11px] text-slate-400">
        <button onClick={load} className="hover:text-slate-700">↻ refresh</button>
      </div>
    </div>
  );
}
